import {
  type DnDDropInfo,
  type Entities,
  type Entity,
  type EntityOrientation,
  EntityType,
  type MainSettings,
  type ModuleEntity,
  type WithId
} from "@client/types";
import { pickBy } from "lodash";
import { insert, move, remove } from "./array";
import { gte, roundTo } from "./math";
import { generateId } from "./random";

export const COLUMN_WIDTH_PERCENTAGE = 8.333;
export const MIN_COLUMN_WIDTH_PERCENTAGE = 5;

export const canDrop = (width: number): boolean => gte(width, COLUMN_WIDTH_PERCENTAGE * 2);

export const getLayoutWidths = (layout: number[]) => {
  const weight = 100 / layout.reduce((res, w) => res + w, 0);
  return layout.map((l) => roundTo(l * weight, 2));
};

export const createPlace = (id: "main" | "footer" | "sidePanel" | "header" | "popups"): Entity => ({
  id,
  parentId: null,
  childrenIds: [],
  settings: null,
  orientation: "vertical",
  type: EntityType.Place,
  dimensionSize: null
});

export const iterateModules = (entities: Entities, enter: (entity: ModuleEntity) => void) =>
  Object.keys(entities)
    .filter((key) => isModuleEntity(entities[key]))
    .forEach((key) => enter(entities[key] as ModuleEntity));

export const getChildEntityTypes = (type: EntityType): EntityType[] | null => {
  switch (type) {
    case EntityType.Place:
      return [EntityType.Block];
    case EntityType.Block:
      return [EntityType.Row, EntityType.Container, EntityType.Module];
    case EntityType.Row:
      return [EntityType.Column];
    case EntityType.Column:
      return [EntityType.Module, EntityType.Container];
    case EntityType.Container:
      return [EntityType.Module, EntityType.Container, EntityType.Row];
    case EntityType.Module:
      return [EntityType.ModuleItem];
    case EntityType.Popup:
      return [EntityType.Row];
    default:
      return null;
  }
};

export const getSiblingEntityTypes = (type: EntityType): EntityType[] => {
  switch (type) {
    case EntityType.Place:
      return [EntityType.Place];
    case EntityType.Block:
      return [EntityType.Block];
    case EntityType.Row:
      return [EntityType.Row, EntityType.Container, EntityType.Module];
    case EntityType.Column:
      return [EntityType.Column];
    case EntityType.Container:
      return [EntityType.Module, EntityType.Container, EntityType.Row];
    case EntityType.Module:
      return [EntityType.Module, EntityType.Container, EntityType.Row];
    case EntityType.Popup:
      return [EntityType.Popup];
    default:
      return [];
  }
};

export const getParentEntityTypes = (type: EntityType): EntityType[] | null => {
  switch (type) {
    case EntityType.Block:
      return [EntityType.Place];
    case EntityType.Row:
      return [EntityType.Block, EntityType.Container];
    case EntityType.Column:
      return [EntityType.Row];
    case EntityType.Module:
      return [EntityType.Column, EntityType.Container, EntityType.Block];
    case EntityType.ModuleItem:
      return [EntityType.Module];
    case EntityType.Container: {
      return [EntityType.Block, EntityType.Column, EntityType.Container];
    }
    default:
      return null;
  }
};

export const getAllParentEntityTypes = (type: EntityType): EntityType[] => {
  let types = [type];
  let index = 0;
  while (true) {
    const pt = getParentEntityTypes(types[index]);
    if (pt == null) break;

    types.push(...pt);

    index++;
  }
  return types.reverse();
};

export const getEntityEntities = (entities: Entities, id: string, opts?: { excludeRoot: boolean }): Entities => {
  const result: Entities = {};
  const queue = [id];

  while (queue.length) {
    const cid = queue.shift()!;

    const entity = {
      ...entities[cid]
    };

    if (entity.childrenIds) {
      queue.push(...entity.childrenIds);
    }

    result[cid] = entity;
  }

  result[id] = {
    ...result[id],
    parentId: null
  };

  if (opts?.excludeRoot) {
    delete result[id];
    Object.keys(result).forEach((x) => {
      if (result[x].parentId === id) result[x].parentId = null;
    });
  }

  return result;
};

export const isModuleEntity = (entity: Entity): entity is ModuleEntity => {
  const me = entity as ModuleEntity;
  return "moduleType" in me;
};

export const findFirstEntity = (
  entities: Entities,
  id: string,
  matchFn: (entity: Entity | ModuleEntity) => boolean
): Entity | ModuleEntity | null => {
  const queue = [id];
  while (queue.length) {
    const cid = queue.shift()!;

    const entity = entities[cid];
    if (matchFn(entity)) return entity;
    if (entity.childrenIds) {
      queue.push(...entity.childrenIds);
    }
  }
  return null;
};

export const getEntitySelector = (id?: string) => (id ? `[data-eid="${id}"]` : "[data-eid]");

/**
 * Does not modify the object
 */
export const addEntity = (entities: Entities, entity: Entity, parentIndex?: number, parentId?: string): Entities => {
  const pid = parentId ?? entity.parentId;
  const result = {
    ...entities,
    [entity.id]: {
      ...entity,
      parentId: pid
    }
  };

  if (pid) {
    const parent = entities[pid];

    if (parent) {
      result[pid] = {
        ...parent,
        childrenIds: insert(parent.childrenIds || [], entity.id, parentIndex ?? (parent.childrenIds || []).length)
      };
    }
  }
  return result;
};

export const wrapChildren = (entities: Entities, id: string, createEntity: () => Entity): Entities => {
  const childrenIds = entities[id].childrenIds;
  if (childrenIds) {
    const wrapEntity = createEntity();
    wrapEntity.parentId = id;
    entities = addEntity(entities, wrapEntity);
    for (let i = 0; i < childrenIds.length; i++) {
      entities = moveEntity(entities, {
        id: childrenIds[i],
        index: i,
        newParentId: wrapEntity.id
      });
    }
  }
  return entities;
};

export const wrapEntity = (entities: Entities, id: string, createEntity: () => Entity): Entities => {
  const wrapEntity = createEntity();
  wrapEntity.parentId = entities[id].parentId!;

  entities = dropEntity(entities, wrapEntity, {
    id,
    isAfter: true
  });
  entities = moveEntity(entities, {
    id,
    newParentId: wrapEntity.id,
    index: 0
  });
  return entities;
};

export const getDropIndex = (dragEntity: WithId, ids: string[] | null, dropInfo?: DnDDropInfo | null): number => {
  if (!ids) return 0;
  if (!dropInfo) return ids.length;

  const targetIds = ids.filter((x) => x !== dragEntity.id);
  const dropIndex = targetIds.findIndex((id) => id === dropInfo.id);
  return dropIndex + (dropInfo.isAfter ? 1 : 0);
};

export const dropEntity = (entities: Entities, entity: Entity, dropInfo?: DnDDropInfo | null): Entities => {
  const parent = entities[entity.parentId!];
  const parentIndex = dropInfo ? getDropIndex(entity, parent.childrenIds, dropInfo) : undefined;
  return addEntity(entities, entity, parentIndex, entity.parentId!);
};

const _cloneEntity = (entities: Entities, id: string, newParentId: string): string => {
  const cloneId = generateId();
  const clone = {
    ...entities[id],
    id: cloneId,
    parentId: newParentId
  };
  entities[cloneId] = clone;
  if (clone.childrenIds) {
    clone.childrenIds = clone.childrenIds.map((cid) => _cloneEntity(entities, cid, cloneId));
  }
  return cloneId;
};

/**
 * Does not modify the object
 */
export const cloneEntity = (entities: Entities, id: string): Entities => {
  const entity = entities[id];
  if (!entity.parentId) throw new Error("BUG");

  const copy = { ...entities };

  const newId = _cloneEntity(copy, id, entity.parentId);

  const parentIndex = copy[entity.parentId].childrenIds!.indexOf(id);
  copy[entity.parentId] = {
    ...copy[entity.parentId],
    childrenIds: insert(copy[entity.parentId].childrenIds!, newId, parentIndex + 1)
  };

  return copy;
};

/**
 * Does not modify the object.
 *
 * @param a If item from b exists here and is equal, then a is used
 * @param b Result
 */
export const mergeEntities = (a: Entities, b: Entities): Entities =>
  Object.keys(b).reduce((res, key) => {
    const item = key in a && a[key] === b[key] ? a[key] : b[key];
    res[key] = item;
    return res;
  }, {});

export const getNewEntities = (oldEntities: Entities, newEntities: Entities): Entities =>
  pickBy(newEntities, (x) => !(x.id in oldEntities));

export const mergeEntitiesIn = (
  target: Entities,
  args: { id: string; parentId: string; position?: number; entities: Entities }
): Entities => {
  const result = {
    ...target,
    ...args.entities
  };

  if (!result[args.parentId]) throw new Error("BUG");

  const childrenIds = result[args.parentId].childrenIds ?? [];
  const position = args.position ?? childrenIds.length;
  childrenIds.splice(position, 0, args.id);

  result[args.parentId].childrenIds = childrenIds;

  result[args.id] = {
    ...result[args.id],
    parentId: args.parentId
  };

  return result;
};

const _deleteEntity = (entities: Entities, id: string) => {
  const entity = entities[id];
  if (entity.childrenIds) entity.childrenIds.forEach((cid) => _deleteEntity(entities, cid));
  delete entities[id];
};

/**
 * Does not modify the object
 */
export const deleteEntityChildren = (entities: Entities, id: string): Entities => {
  const copy = { ...entities };

  const entity = copy[id];
  if (entity.childrenIds) entity.childrenIds.forEach((cid) => _deleteEntity(copy, cid));

  return copy;
};

/**
 * Does not modify the object
 */
export const deleteEntity = (entities: Entities, id: string): Entities => deleteEntities(entities, [id]);

export const deleteEntities = (a: Entities, ids: string[]): Entities => {
  const copy = { ...a };

  for (const id of ids) {
    if (!(id in a)) continue;

    deleteEntityInner(copy, id);
  }

  return copy;
};

const deleteEntityInner = (entities: Entities, id: string) => {
  const entity = entities[id];
  _deleteEntity(entities, id);

  if (entity.parentId && entity.parentId in entities) {
    // the second condition is a protection for broken snapshots (when links were added)
    entities[entity.parentId] = {
      ...entities[entity.parentId],
      childrenIds: remove(entities[entity.parentId].childrenIds!, id)
    };
  }
};

export interface MoveEntityArgs {
  newParentId: string;
  id: string;
  index: number;
}

/**
 * Does not modify the object
 */
export const moveEntity = (entities: Entities, args: MoveEntityArgs): Entities => {
  const entity = entities[args.id];
  if (!entity.parentId) throw new Error("BUG");

  const oldParent = entities[entity.parentId];
  if (!oldParent.childrenIds) throw new Error("BUG");

  const newParent = entities[args.newParentId];

  if (oldParent.id === newParent.id) {
    return {
      ...entities,
      [oldParent.id]: {
        ...oldParent,
        childrenIds: move(oldParent.childrenIds!, args.id, args.index)
      }
    };
  }

  return {
    ...entities,
    [oldParent.id]: {
      ...oldParent,
      childrenIds: remove(oldParent.childrenIds, entity.id)
    },
    [newParent.id]: {
      ...newParent,
      childrenIds: insert(newParent.childrenIds || [], entity.id, args.index)
    },
    [entity.id]: {
      ...entity,
      parentId: newParent.id
    }
  };
};

/**
 * Does not modify the object
 */
export const updateEntity = (entities: Entities, id: string, updateFn: (e: Entity) => Entity): Entities =>
  id in entities
    ? {
        ...entities,
        [id]: updateFn(entities[id])
      }
    : entities;

export const updateEntities = (
  entities: Entities,
  matchFn: (e: Entity) => boolean,
  updateFn: (e: Entity) => Entity
): Entities =>
  Object.keys(entities).reduce((res, id) => {
    res[id] = matchFn(entities[id]) ? updateFn(entities[id]) : entities[id];
    return res;
  }, {});

export const getEntityOrientation = (type: EntityType): EntityOrientation => {
  switch (type) {
    case EntityType.Place:
    case EntityType.Popup:
    case EntityType.Block:
    case EntityType.Column:
    case EntityType.Container:
    case EntityType.Module:
      return "vertical";
    default:
      return "horizontal";
  }
};

export const addParentEntity = (
  entities: Entities,
  id: string,
  type: EntityType,
  settings: MainSettings = {}
): Entities => {
  if (id in entities) return entities;

  const childrenIds = Object.keys(entities).filter(
    (k) => (entities[k].type !== EntityType.Place && !entities[k].parentId) || entities[k].parentId === id
  );

  childrenIds.forEach((cid) => {
    entities[cid].parentId = id;
  });

  entities[id] = {
    id,
    childrenIds: childrenIds,
    settings,
    type,
    parentId: ""
  };

  return entities;
};

export const equalize = (entities: Entities, parentId: string): Entities => {
  const entity = entities[parentId];
  if (getEntityOrientation(entity.type) === "horizontal" && entity.childrenIds) {
    const childrenCount = entity.childrenIds.length;

    for (const childId of entity.childrenIds) {
      entities[childId] = {
        ...entities[childId],
        dimensionSize: roundTo(100 / childrenCount, 2)
      };
    }
  }
  return {
    ...entities
  };
};

export const setRootEntity = (entities: Entities, root: Entity): Entities => {
  const toLink = Object.keys(entities).filter((x) => !entities[x].parentId);

  if (toLink.length) {
    return {
      ...entities,
      ...toLink.reduce((res, x) => {
        res[x] = {
          ...entities[x],
          parentId: root.id
        };

        return res;
      }, {}),
      [root.id]: {
        ...root,
        childrenIds: toLink
      }
    };
  }

  return entities;
};
